import { useRef, useState, Fragment  } from 'react'

import { controlIcons, tableIcons } from '../../../constants/assets'
import { tripStatusLabels, tripStatuses } from '../../../data/bookings'
import { useClickOutside } from '../../../hooks/useClickOutside'
import type { TripStatus } from '../../../types'
import { cn } from '../../../utils/cn'

type TripStyle = {
  chip: string
  text: string
  /** Each state uses a differently sized caret in the design. */
  caret: number
  /** `on-trip` uses a traced glyph that needs the design's inset and flip. */
  tracedCaret?: boolean
}

const TRIP_STYLES: Record<TripStatus, TripStyle> = {
  completed: {
    chip: 'bg-dash-pill-completed',
    text: 'text-[14px] font-normal text-dash-pill-ink',
    caret: 16,
  },
  'on-trip': {
    chip: 'bg-dash-pill-ontrip',
    text: 'text-[12px] font-semibold text-dash-pill-ontrip-ink',
    caret: 12,
    tracedCaret: true,
  },
  upcoming: {
    chip: 'bg-dash-pill-upcoming',
    text: 'text-[14px] font-normal text-dash-pill-ink',
    caret: 20,
  },
  cancelled: {
    chip: 'border-[#F8D7DA] bg-[#F8D7DA] text-[#721C24]',
    text: 'text-[#721C24]',
    caret: 0,
  },
  confirmed: {
    chip: 'border-[#D1E7DD] bg-[#D1E7DD] text-[#0F5132]',
    text: 'text-[#0F5132]',
    caret: 0,
  },
  pending: {
    chip: 'border-[#FFF3CD] bg-[#FFF3CD] text-[#664D03]',
    text: 'text-[#664D03]',
    caret: 0,
  },
  incomplete: {
    chip: 'border-[#E2E3E5] bg-[#E2E3E5] text-[#41464B]',
    text: 'text-[#41464B]',
    caret: 0,
  },
}

type TripStatusSelectProps = {
  status: TripStatus
  /** Names the row this control belongs to, so the label is not just "Completed". */
  bookingLabel: string
  onChange: (status: TripStatus) => void
}

export const TripStatusSelect = ({ status, bookingLabel, onChange }: TripStatusSelectProps) => {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useClickOutside(containerRef, () => setOpen(false), open)

  const style = TRIP_STYLES[status]

  return (
    <div ref={containerRef} className="relative w-[101px]">
      <button
        type="button"
        aria-label={`Trip status for ${bookingLabel}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((isOpen) => !isOpen)}
        className={cn(
          'flex h-[30px] w-full items-center justify-center rounded-[12px] px-[10px]',
          style.chip,
        )}
      >
        <span className={cn('whitespace-nowrap font-host leading-normal', style.text)}>
          {tripStatusLabels[status]}
        </span>

        {style.tracedCaret ? (
          <span
            aria-hidden
            className="relative block shrink-0 overflow-hidden"
            style={{ width: style.caret, height: style.caret }}
          >
            <span className="absolute bottom-[33.29%] left-[20.72%] right-[21.05%] top-[33.22%] block -scale-y-100">
              <img alt="" className="block size-full max-w-none" src={tableIcons.statusCaretSm} />
            </span>
          </span>
        ) : (
          <img
            alt=""
            aria-hidden
            src={controlIcons.selectCaret}
            style={{ width: style.caret, height: style.caret }}
            className="block max-w-none shrink-0 object-cover"
          />
        )}
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={`Trip status for ${bookingLabel}`}
          className="absolute left-0 top-[calc(100%+6px)] z-20 flex w-full flex-col overflow-hidden rounded-[12px] border border-solid border-dash-field bg-white py-[4px] shadow-dash-card"
        >
          {/* {tripStatuses.map((option) => (
            <li key={option}>
              <button
                type="button"
                role="option"
                aria-selected={option === status}
                onClick={() => {
                  onChange(option)
                  setOpen(false)
                }}
                className={cn(
                  'w-full whitespace-nowrap px-[10px] py-[6px] text-left font-host text-[14px] leading-normal hover:bg-dash-row-head-bg',
                  option === status ? 'font-medium text-dash-link' : 'font-normal text-black',
                )}
              >
                {tripStatusLabels[option]}
              </button>
            </li>
          ))} */}
          {tripStatuses.map((option, index) => (
            <Fragment key={option}>
              {index === 5 && (
                <li
                  role="separator"
                  aria-hidden="true"
                  className="my-[4px]"
                >
                  <hr className="border-0 border-t border-[#E5E7EB]" />
                </li>
              )}

              <li>
                <button
                  type="button"
                  role="option"
                  aria-selected={option === status}
                  onClick={() => {
                    onChange(option)
                    setOpen(false)
                  }}
                  className={cn(
                    'w-full whitespace-nowrap px-[10px] py-[6px] text-left font-host text-[14px] leading-normal hover:bg-dash-row-head-bg',
                    option === status
                      ? 'font-medium text-dash-link'
                      : 'font-normal text-black',
                  )}
                >
                  {tripStatusLabels[option]}
                </button>
              </li>
            </Fragment>
          ))}
        </ul>
      )}
    </div>
  )
}
